import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { DropdownContext } from 'components/DropdownContext';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import type { IUseAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';

import { useCallback, useMemo } from 'react';

import { isSystemConstructionType } from './helpers';
import { useRemoveConstructionType } from './hooks/useRemoveConstructionType';
import type { TCatalogConstructionTypeRow } from './types';

import Styles from './catalogConstructionTypes.module.css';

interface ICatalogConstructionTypesRowMenu {
    data: TCatalogConstructionTypeRow;
    createOrEditModalHandler: (state: boolean) => void;
    setDataEditModal: React.Dispatch<React.SetStateAction<TCatalogConstructionTypeRow | null>>;
    onRemoveSuccess: () => void;
    catalogsRules: IUseAccess['catalogs'];
}

export const CatalogConstructionTypesRowMenu: React.FC<ICatalogConstructionTypesRowMenu> = ({
    data,
    createOrEditModalHandler,
    setDataEditModal,
    onRemoveSuccess,
    catalogsRules,
}) => {
    const {
        catalogRemoveConstructionTypeRequest,
        catalogRemoveConstructionTypeStatus,
        catalogRemoveConstructionTypeData,
        catalogRemoveConstructionTypeError,
        isSubmitted,
        handleCloseModalError,
        handleCreateReport,
    } = useRemoveConstructionType(data.id, onRemoveSuccess);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => catalogRemoveConstructionTypeRequest(),
        });

    const isDisabledDeleteButton = catalogRemoveConstructionTypeStatus === RequestStatus.FETCHING;
    const isSystemType = isSystemConstructionType(data.code);
    const isEditDisabled = isSystemType || !catalogsRules.canEdit;

    const onOpenEditModalHandler = useCallback(() => {
        if (isSystemConstructionType(data.code)) {
            return;
        }

        createOrEditModalHandler(true);
        setDataEditModal(data);
    }, [createOrEditModalHandler, data, setDataEditModal]);

    const items = useMemo(
        () => [
            {
                label: 'Редактировать',
                action: onOpenEditModalHandler,
                disabled: isEditDisabled,
            },
            {
                label: 'Удалить',
                disabled: isDisabledDeleteButton || !catalogsRules.canDelete,
                action: handleOpenConfirmationDialog,
            },
        ],
        [
            catalogsRules.canDelete,
            handleOpenConfirmationDialog,
            isDisabledDeleteButton,
            isEditDisabled,
            onOpenEditModalHandler,
        ],
    );

    return (
        <>
            <DropdownContext
                items={items}
                direction="downStartRight"
                wrapperClassName={Styles.wrapperDropdown}
                isDisabled={isEditDisabled && !catalogsRules.canDelete}
            />

            <ConfirmationDialog
                textHeader="Удаление записи"
                textConfirmation="Вы уверены, что хотите удалить запись?"
                confirmButtonText="Удалить"
                cancelButtonText="Отменить"
                onSubmit={handleActionQuery}
                isOpened={isOpenConfirmationDialog}
                onClose={handleCloseConfirmationDialog}
            />

            <ErrorHandlingModal
                titleText="Удаление вида строительства"
                customButtonText="Понятно"
                viewButton="primary"
                isSubmitted={isSubmitted}
                statusRequest={catalogRemoveConstructionTypeStatus}
                dataRequest={catalogRemoveConstructionTypeData?.catalogRemoveConstructionType}
                errorsRequest={catalogRemoveConstructionTypeError}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
        </>
    );
};

CatalogConstructionTypesRowMenu.displayName = 'CatalogConstructionTypesRowMenu';
